'use client';
import { useState } from 'react';
import * as Sentry from '@sentry/nextjs';
import { useRouter } from 'src/navigation';
import styles from './audio-guide-search.module.scss';
import pushToDataLayer from 'lib/analytics/pushToDataLayer';
import SearchBar from 'components/controls/search-bar';
import HeadphonesIcon from 'assets/icons/headphones-icon.svg';
import ErrorMessageBox from 'components/controls/error-message';
import { useTranslations } from 'next-intl';
import { useLocale } from 'next-intl';
import { stopExists } from './data';

const Search = ({
	stopNumber,
	pageUrl = '/audio-guide',
	replaceRoute = false,
}: IProps) => {
	const lang = useLocale();
	const router = useRouter();
	const t = useTranslations('audio');
	const [stop, setStopNumber] = useState(stopNumber);
	const [showAlert, setShowAlert] = useState(false);
	const [isSearching, setIsSearching] = useState(false);

	const handleSelection = async (event) => {
		if (!stop) {
			setShowAlert(false);
			return null;
		}

		// clean input first
		let tempStopNumber = stop; // save as local since useState is asynchronous
		tempStopNumber = tempStopNumber.replace(/[^0-9]/g, '');
		setStopNumber(tempStopNumber);

		event.preventDefault();

		try {
			setIsSearching(true);
			setShowAlert(false);
			const exists = await stopExists(tempStopNumber);
			if (exists) {
				replaceRoute
					? router.replace(`${pageUrl}/${tempStopNumber}`, { locale: lang })
					: router.push(`${pageUrl}/${tempStopNumber}`, { locale: lang });
			} else {
				setShowAlert(true);
				pushToDataLayer({
					event: 'audioGuide.stopNotFound',
					audioGuide: {
						stopNumber: tempStopNumber,
					},
				});
			}
			setIsSearching(false);
		} catch (error) {
			setIsSearching(false);
			setShowAlert(true);
			Sentry.captureException(error);
		}
	};

	const handleKeyDown = (event) => {
		if (event.key === 'Enter') {
			handleSelection(event);
		}

		setShowAlert(false);
	};

	return (
		<div className={`${styles.inputWrapper}`}>
			<SearchBar
				debounceTimeout={0}
				placeholderText={t('tourSearchCTA')}
				initialQuery={stop}
				onChange={(event) => setStopNumber(event.target.value)}
				onKeyDown={handleKeyDown}
				onButtonClick={handleSelection}
				numericOnly={true}
				icon={
					<div className={styles.searchIcon}>
						<HeadphonesIcon />
					</div>
				}
				isSearching={isSearching}
			/>
			{showAlert && (
				<div className={styles.errorWrapper}>
					<ErrorMessageBox message={t('stopNotFound')} />
				</div>
			)}
		</div>
	);
};

interface IProps {
	stopNumber?: string;
	replaceRoute?: boolean;
	pageUrl?: string;
}

export default Search;
